import Loop24 from '@carbon/icons-react/es/loop/24';
import Tag24 from '@carbon/icons-react/es/tag/24';
import Timer24 from '@carbon/icons-react/es/timer/24';
import React, { FC, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Project, ProjectFinancialsContractType } from 'api/project';
import { ReactComponent as NonBillable16 } from 'assets/svgs/non-billable-16.svg';
import { PageLoading } from 'components/atoms/loading';
import { Page } from 'components/molecules/page';
import { Sticky } from 'components/molecules/sticky';
import { ProductiveHeading05 } from 'components/styles';
import { ProjectFinancialsSettingsContext } from 'contexts/project-financials-settings';
import { useGetAllActiveUsersMap } from 'hooks/use-idb';
import { useTranslate, TranslationKey } from 'hooks/use-translation';
import { useProjectsBudgetAndRateSettingsQuery } from 'queries/project';
import { BillingInfo } from './billing-info';
import { TeamMembersRateInfo } from './member-rate-info-table';

const PageContent = styled(Page.Content)`
  padding: var(--spacing-06);
  width: 85%;
  > .ant-row {
    padding: var(--spacing-03);
  }
`;
const BillingTypeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-04);
  margin-bottom: var(--spacing-07);
  margin-left: var(--spacing-05);
`;
const Header = styled.div`
  ${ProductiveHeading05};
`;
const BillingType = styled(Header)`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  font-weight: var(--heading-8-weight);
`;
const NonBillable = styled(NonBillable16)`
  width: var(--spacing-06);
  height: var(--spacing-06);
`;

const getBillingMethodIcon = (billingMethod: ProjectFinancialsContractType) => {
  switch (billingMethod) {
    case ProjectFinancialsContractType.FIXED:
      return <Tag24 />;
    case ProjectFinancialsContractType.TM:
      return <Timer24 />;
    case ProjectFinancialsContractType.SUBSCRIPTION:
      return <Loop24 />;
    case ProjectFinancialsContractType.NONBILLABLE:
      return <NonBillable />;
    default:
      return null;
  }
};

export const BudgetAndRates: FC<{ project: Project }> = ({ project }) => {
  const projectId = Number(project.projectId);
  const { permissionsMap } = useContext(ProjectFinancialsSettingsContext);
  const {
    projectProfitability: { canRead: canReadCostRates },
  } = permissionsMap;

  const { data, isLoading } = useProjectsBudgetAndRateSettingsQuery(projectId);
  const translate = useTranslate();
  const usersMap = useGetAllActiveUsersMap();
  const canAccessMemberRateTable =
    data?.financialContractType !== ProjectFinancialsContractType.NONBILLABLE ||
    (data?.financialContractType ===
      ProjectFinancialsContractType.NONBILLABLE &&
      canReadCostRates);

  const customBillRateLookUp = useMemo(
    () =>
      (data?.projectCustomRates ?? []).reduce(
        (map, entry) => ({
          ...map,
          [`${entry.sourceId}${
            entry.replacedUserId ? `_${entry.replacedUserId}_` : '_'
          }${entry.sourceType}`]: {
            ...entry,
            updatedBy: usersMap[entry.updatedBy || 0] ?? null,
          },
        }),
        {},
      ),
    [data?.projectCustomRates, usersMap],
  );

  return (
    <Page>
      <Sticky top={0}>
        <Page.Header
          $white
          title={
            <Page.Header.Title>
              {translate('project.settings.budget.rate.title')}
            </Page.Header.Title>
          }
        />
      </Sticky>
      {!isLoading && data ? (
        <PageContent>
          <BillingTypeWrapper>
            <Header>{`${translate(
              'project.settings.budget.rate.billing.method',
            )}:`}</Header>
            <BillingType>
              {getBillingMethodIcon(data.financialContractType)}
              {translate(
                `project.settings.billing.type.${data?.financialContractType}.title` as TranslationKey,
              )}
            </BillingType>
          </BillingTypeWrapper>

          <BillingInfo
            projectId={projectId}
            projectCurrencySymbol={project.currency?.currencySymbol}
            data={data}
          />
          {canAccessMemberRateTable ? (
            <TeamMembersRateInfo
              showHeader
              readonly
              overrideBillRate={!!data.projectCustomRates.length}
              customBillRateMap={customBillRateLookUp}
              projectId={projectId}
              data={data}
              rateCardVersionId={data.rateCardVersionId}
            />
          ) : null}
        </PageContent>
      ) : (
        <PageLoading />
      )}
    </Page>
  );
};
